import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, apiClient, clearUserInfo, getUserInfo, setUserInfo } from '../../api/client';
import { useTheme, Theme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import NotificationPreferences, { type NotificationPrefsValue } from '../../components/preferences/NotificationPreferences';
import { ConfirmModal } from '../../components/ui';
import PageHeader from '../../components/PageHeader';
import SettingsCard from '../../components/SettingsCard';
import '../../App.css';
import { translateError } from '../../utils/errorMessages';

interface IUserPreferences {
  suppressNotifications?: boolean;
  eventReminders?: 'off' | 'special' | 'all';
  notifications?: NotificationPrefsValue;
}

interface IProfileResponse {
  _id: string;
  name: string;
  email: string;
  preferences?: IUserPreferences;
}

const UserPreferencesPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { user, isAdmin, isOwner, hasPermission } = useAuth();
  const canManageOrg = hasPermission('organization:write');
  // Legacy keys, kept only so an untouched account keeps behaving as before.
  // Saving the matrix drops them server-side.
  const [legacySuppressed, setLegacySuppressed] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState<NotificationPrefsValue | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [leaveOrgConfirmOpen, setLeaveOrgConfirmOpen] = useState(false);
  const [leaveOrgLoading, setLeaveOrgLoading] = useState(false);
  const [erasureConfirmOpen, setErasureConfirmOpen] = useState(false);
  const [erasureLoading, setErasureLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Calendar sync state
  const [calendarFeedUrl, setCalendarFeedUrl] = useState<string | null>(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Admin calendar sync state
  const [adminCalendarFeedUrl, setAdminCalendarFeedUrl] = useState<string | null>(null);
  const [adminCalendarLoading, setAdminCalendarLoading] = useState(false);
  const [adminCopied, setAdminCopied] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.profile.get();
        const data = response.data as IProfileResponse;

        if (data.preferences) {
          setLegacySuppressed(data.preferences.suppressNotifications || false);
          setNotifPrefs(
            data.preferences.notifications ?? {
              v: 1,
              channels: {},
              types: {},
              eventRemindersScope: data.preferences.eventReminders ?? 'all',
            },
          );
        }
      } catch (err: any) {
        console.log("Could not fetch profile preferences", err);
      }
    };
    fetchProfile();
  }, []);

  // Calendar sync
  const fetchCalendarToken = useCallback(async () => {
    try {
      const response = await api.calendar.getToken();
      const data = response.data;
      setCalendarFeedUrl(data.feedUrl);
    } catch {
      // Calendar feature may not be available
    }
  }, []);

  useEffect(() => {
    fetchCalendarToken();
  }, [fetchCalendarToken]);

  // Admin calendar sync
  const fetchAdminCalendarToken = useCallback(async () => {
    try {
      const response = await api.calendar.getAdminToken();
      setAdminCalendarFeedUrl(response.data.feedUrl);
    } catch {
      // Admin calendar feature may not be available
    }
  }, []);

  useEffect(() => {
    if (canManageOrg) {
      fetchAdminCalendarToken();
    }
  }, [isAdmin, isOwner, fetchAdminCalendarToken]);

  const handleEnableCalendar = async () => {
    setCalendarLoading(true);
    setMessage(null);
    setError(null);
    try {
      const response = await api.calendar.generateToken();
      setCalendarFeedUrl(response.data.feedUrl);
      setMessage(t('calendarSync.tokenGenerated'));
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleDisableCalendar = async () => {
    setCalendarLoading(true);
    setMessage(null);
    setError(null);
    try {
      await api.calendar.deleteToken();
      setCalendarFeedUrl(null);
      setMessage(t('calendarSync.tokenDeleted'));
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleCopyUrl = () => {
    if (calendarFeedUrl) {
      navigator.clipboard.writeText(calendarFeedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getGoogleCalendarUrl = () => {
    if (!calendarFeedUrl) return '';
    const webcalUrl = calendarFeedUrl.replace(/^https?:\/\//, 'webcal://');
    const params = new URLSearchParams({ cid: webcalUrl });
    if (user?.email) {
      params.set('authuser', user.email);
    }
    return `https://calendar.google.com/calendar/r?${params.toString()}`;
  };

  const getAppleCalendarUrl = () => {
    if (!calendarFeedUrl) return '';
    return calendarFeedUrl.replace(/^https?:\/\//, 'webcal://');
  };

  // Admin calendar handlers
  const handleEnableAdminCalendar = async () => {
    setAdminCalendarLoading(true);
    setMessage(null);
    setError(null);
    try {
      const response = await api.calendar.generateAdminToken();
      setAdminCalendarFeedUrl(response.data.feedUrl);
      setMessage(t('adminCalendarSync.tokenGenerated'));
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setAdminCalendarLoading(false);
    }
  };

  const handleDisableAdminCalendar = async () => {
    setAdminCalendarLoading(true);
    setMessage(null);
    setError(null);
    try {
      await api.calendar.deleteAdminToken();
      setAdminCalendarFeedUrl(null);
      setMessage(t('adminCalendarSync.tokenDeleted'));
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setAdminCalendarLoading(false);
    }
  };

  const handleAdminCopyUrl = () => {
    if (adminCalendarFeedUrl) {
      navigator.clipboard.writeText(adminCalendarFeedUrl);
      setAdminCopied(true);
      setTimeout(() => setAdminCopied(false), 2000);
    }
  };

  const getAdminGoogleCalendarUrl = () => {
    if (!adminCalendarFeedUrl) return '';
    const webcalUrl = adminCalendarFeedUrl.replace(/^https?:\/\//, 'webcal://');
    const params = new URLSearchParams({ cid: webcalUrl });
    if (user?.email) params.set('authuser', user.email);
    return `https://calendar.google.com/calendar/r?${params.toString()}`;
  };

  const getAdminAppleCalendarUrl = () => {
    if (!adminCalendarFeedUrl) return '';
    return adminCalendarFeedUrl.replace(/^https?:\/\//, 'webcal://');
  };

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setError(null);

    try {
      const response = await api.profile.update({
        preferences: {
          theme,
          // Sending `notifications` makes the backend drop the two legacy
          // keys, so the matrix becomes authoritative from here on.
          notifications: notifPrefs,
        },
      } as any);

      // The kill switch is gone once the matrix is saved; reflect that locally
      // so the UI doesn't keep rendering every row as off.
      setLegacySuppressed(false);

      // Update local storage
      const userInfo = getUserInfo();
      if (userInfo) {
        const updatedUserInfo = { ...userInfo, ...(response.data as object) };
        setUserInfo(updatedUserInfo);
      }

      setMessage(t('preferences.updateSuccess'));
      setLoading(false);
    } catch (err: any) {
      setError(translateError(err, t));
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    setMessage(null);
    setError(null);

    try {
      const response = await apiClient.get('/api/users/my-data', {
        responseType: 'blob',
      });

      // Create download link
      const blob = new Blob([response.data as BlobPart], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `my-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage(t('gdpr.exportSuccess'));
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setExporting(false);
    }
  };

  const handleLeaveOrganization = async () => {
    setLeaveOrgLoading(true);
    setMessage(null);
    setError(null);

    try {
      await api.profile.leaveOrganization();
      setLeaveOrgConfirmOpen(false);
      setMessage(t('gdpr.leaveOrgSuccess'));
      // Optionally redirect or refresh data
      setTimeout(() => window.location.href = '/', 2000);
    } catch (err: any) {
      setError(translateError(err, t));
      setLeaveOrgLoading(false);
    }
  };

  const handleCloseAccount = async () => {
    setDeleting(true);
    setMessage(null);
    setError(null);

    try {
      await api.profile.closeAccount();

      setDeleteConfirmOpen(false);
      // Clear local storage and redirect to home
      clearUserInfo();
      alert(t('gdpr.closeAccountSuccess'));
      window.location.href = '/';
    } catch (err: any) {
      setError(translateError(err, t));
      setDeleting(false);
    }
  };

  const handleRequestErasure = async () => {
    setErasureLoading(true);
    setMessage(null);
    setError(null);

    try {
      await api.profile.requestErasure();
      setErasureConfirmOpen(false);
      setMessage(t('gdpr.requestErasureSuccess'));
    } catch (err: any) {
      setError(translateError(err, t));
      setErasureLoading(false);
    }
  };

  // Legacy compatibility: handleDeleteAccount now calls closeAccount
  const handleDeleteAccount = handleCloseAccount;

  // Side column: self-contained widgets, each with its own actions. Kept out of
  // the main <form> deliberately — none of them are saved by its Save button.
  // Section definitions. Only Appearance and Notifications are backed by the
  // profile form, so only those carry a Save; Calendar and Data have their own
  // per-action buttons.
  return (
    <div className="admin-page-content preferences-page">
      <PageHeader title={t('preferences.title')} helpKey="help.user.preferences" />
      {message && <p className="success-message">{message}</p>}
      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      <div className="settings-grid">
        {/* Compact groups first: they pack into one short row, so the wide
            notifications card below still starts high on the page. */}
        <SettingsCard
          title={t('preferences.sectionAppearance')}
          description={t('preferences.sectionAppearanceDesc')}
          actions={
            <Button type="submit" form="prefs-form" loading={loading}>
              {loading ? t('common.updating') : t('common.save')}
            </Button>
          }
        >
          <div className="form-group">
            <label htmlFor="theme">{t('preferences.theme')}</label>
            <select
              id="theme"
              value={theme}
              onChange={(e) => setTheme(e.target.value as Theme)}
            >
              <option value="dark">{t('preferences.themeDark')}</option>
              <option value="light">{t('preferences.themeLight')}</option>
            </select>
          </div>
        </SettingsCard>

        <SettingsCard title={t('preferences.sectionCalendar')}>
          {/* Calendar Sync Section */}
                  <div className="settings-card__block">
                    <h3>{t('calendarSync.title')}</h3>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                      {t('calendarSync.description')}
                    </p>

                    {calendarFeedUrl ? (
                      <>
                        <p style={{ color: 'var(--success-color, #27ae60)', marginBottom: '1rem', fontWeight: 500 }}>
                          {t('calendarSync.feedEnabled')}
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                          <a
                            href={getGoogleCalendarUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                          >
                            <Button
                              type="button"
                              fullWidth
                              style={{ backgroundColor: '#4285f4', color: 'white', border: 'none' }}
                            >
                              {t('calendarSync.addToGoogle')}
                            </Button>
                          </a>

                          <a
                            href={getAppleCalendarUrl()}
                            style={{ textDecoration: 'none' }}
                          >
                            <Button
                              type="button"
                              fullWidth
                              style={{ backgroundColor: '#333', color: 'white', border: 'none' }}
                            >
                              {t('calendarSync.addToApple')}
                            </Button>
                          </a>

                          <Button
                            type="button"
                            variant="secondary"
                            fullWidth
                            onClick={handleCopyUrl}
                          >
                            {copied ? t('calendarSync.copied') : t('calendarSync.copyUrl')}
                          </Button>
                        </div>

                        <small style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '1rem' }}>
                          {t('calendarSync.syncHelp')}
                        </small>

                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button
                            type="button"
                            variant="secondary"
                            fullWidth
                            loading={calendarLoading}
                            onClick={handleEnableCalendar}
                          >
                            {t('calendarSync.regenerateToken')}
                          </Button>
                          <Button
                            type="button"
                            variant="danger"
                            fullWidth
                            loading={calendarLoading}
                            onClick={handleDisableCalendar}
                          >
                            {t('calendarSync.disableFeed')}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                          {t('calendarSync.noTokenYet')}
                        </p>
                        <Button
                          type="button"
                          fullWidth
                          loading={calendarLoading}
                          onClick={handleEnableCalendar}
                        >
                          {t('calendarSync.generateToken')}
                        </Button>
                      </>
                    )}
                  </div>
          {/* Admin Calendar Sync Section */}
                  {(canManageOrg) && (
                    <div className="settings-card__block">
                      <h3>{t('adminCalendarSync.title')}</h3>
                      <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                        {t('adminCalendarSync.description')}
                      </p>

                      {adminCalendarFeedUrl ? (
                        <>
                          <p style={{ color: 'var(--success-color, #27ae60)', marginBottom: '1rem', fontWeight: 500 }}>
                            {t('adminCalendarSync.feedEnabled')}
                          </p>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                            <a
                              href={getAdminGoogleCalendarUrl()}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ textDecoration: 'none' }}
                            >
                              <Button
                                type="button"
                                fullWidth
                                style={{ backgroundColor: '#4285f4', color: 'white', border: 'none' }}
                              >
                                {t('adminCalendarSync.addToGoogle')}
                              </Button>
                            </a>

                            <a
                              href={getAdminAppleCalendarUrl()}
                              style={{ textDecoration: 'none' }}
                            >
                              <Button
                                type="button"
                                fullWidth
                                style={{ backgroundColor: '#333', color: 'white', border: 'none' }}
                              >
                                {t('adminCalendarSync.addToApple')}
                              </Button>
                            </a>

                            <Button
                              type="button"
                              variant="secondary"
                              fullWidth
                              onClick={handleAdminCopyUrl}
                            >
                              {adminCopied ? t('adminCalendarSync.copied') : t('adminCalendarSync.copyUrl')}
                            </Button>
                          </div>

                          <small style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '1rem' }}>
                            {t('adminCalendarSync.syncHelp')}
                          </small>

                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <Button
                              type="button"
                              variant="secondary"
                              fullWidth
                              loading={adminCalendarLoading}
                              onClick={handleEnableAdminCalendar}
                            >
                              {t('adminCalendarSync.regenerateToken')}
                            </Button>
                            <Button
                              type="button"
                              variant="danger"
                              fullWidth
                              loading={adminCalendarLoading}
                              onClick={handleDisableAdminCalendar}
                            >
                              {t('adminCalendarSync.disableFeed')}
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                            {t('adminCalendarSync.noTokenYet')}
                          </p>
                          <Button
                            type="button"
                            fullWidth
                            loading={adminCalendarLoading}
                            onClick={handleEnableAdminCalendar}
                          >
                            {t('adminCalendarSync.generateToken')}
                          </Button>
                        </>
                      )}
                    </div>
                  )}
        </SettingsCard>

        <SettingsCard title={t('preferences.sectionData')}>
          {/* GDPR Data Management Section */}
                  <div className="gdpr-section">

                    <div className="gdpr-action" style={{ marginBottom: '1.5rem' }}>
                      <h4>{t('gdpr.exportData')}</h4>
                      <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                        {t('gdpr.exportDataDesc')}
                      </p>
                      <Button
                        fullWidth
                        loading={exporting}
                        onClick={handleExportData}
                        style={{
                          backgroundColor: '#3498db',
                          color: 'white',
                          border: 'none',
                        }}
                      >
                        {exporting ? t('gdpr.exporting') : t('gdpr.exportButton')}
                      </Button>
                    </div>

                    <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginTop: '1.5rem' }}>
                      <h3 style={{ marginBottom: '1rem' }}>{t('gdpr.accountActions')}</h3>

                      <div className="gdpr-action" style={{ marginBottom: '1.5rem' }}>
                        <h4>{t('gdpr.leaveOrganization')}</h4>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                          {t('gdpr.leaveOrgDesc')}
                        </p>
                        <Button
                          variant="secondary"
                          fullWidth
                          loading={leaveOrgLoading}
                          onClick={() => setLeaveOrgConfirmOpen(true)}
                        >
                          {leaveOrgLoading ? t('gdpr.deleting') : t('gdpr.leaveOrgButton')}
                        </Button>
                      </div>

                      <div className="gdpr-action" style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{ color: '#f39c12' }}>{t('gdpr.closeAccount')}</h4>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                          {t('gdpr.closeAccountDesc')}
                        </p>
                        <Button
                          variant="danger"
                          fullWidth
                          loading={deleting}
                          onClick={() => setDeleteConfirmOpen(true)}
                        >
                          {deleting ? t('gdpr.deleting') : t('gdpr.closeAccountButton')}
                        </Button>
                      </div>

                      <div className="gdpr-action">
                        <h4 style={{ color: '#e74c3c' }}>{t('gdpr.requestErasure')}</h4>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                          {t('gdpr.requestErasureDesc')}
                        </p>
                        <Button
                          variant="danger"
                          fullWidth
                          loading={erasureLoading}
                          onClick={() => setErasureConfirmOpen(true)}
                        >
                          {erasureLoading ? t('gdpr.deleting') : t('gdpr.requestErasureButton')}
                        </Button>
                      </div>
                    </div>
                  </div>
        </SettingsCard>

        {/* The matrix is 18 rows x 3 channels — it needs the whole row. */}
        <SettingsCard
          title={t('notificationPrefs.title')}
          description={t('notificationPrefs.description')}
          wide
          actions={
            <Button type="submit" form="prefs-form" loading={loading}>
              {loading ? t('common.updating') : t('common.save')}
            </Button>
          }
        >
          <NotificationPreferences
            value={notifPrefs}
            legacySuppressed={legacySuppressed}
            onChange={setNotifPrefs}
          />
        </SettingsCard>
      </div>

      {/*
        One form for both profile-backed cards (theme + notifications), attached
        by `form=` so either card's Save submits the same handler. Rendered
        outside the grid so it never affects layout.
      */}
      <form id="prefs-form" onSubmit={submitHandler} hidden />



      <ConfirmModal
        isOpen={leaveOrgConfirmOpen}
        onClose={() => setLeaveOrgConfirmOpen(false)}
        onConfirm={handleLeaveOrganization}
        title={t('gdpr.leaveOrganization')}
        message={t('gdpr.leaveOrgConfirm')}
        confirmText={t('gdpr.leaveOrgButton')}
        cancelText={t('common.cancel')}
        loading={leaveOrgLoading}
      />

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleCloseAccount}
        title={t('gdpr.closeAccount')}
        message={t('gdpr.closeAccountConfirm')}
        confirmText={t('gdpr.closeAccountButton')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleting}
      />

      <ConfirmModal
        isOpen={erasureConfirmOpen}
        onClose={() => setErasureConfirmOpen(false)}
        onConfirm={handleRequestErasure}
        title={t('gdpr.requestErasure')}
        message={t('gdpr.requestErasureConfirm')}
        confirmText={t('gdpr.requestErasureButton')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={erasureLoading}
      />
    </div>
  );
}

export default UserPreferencesPage;